if (!customElements.get('blog-filter')) {
  customElements.define(
    'blog-filter',
    class BlogFilter extends HTMLElement {
      connectedCallback() {
        const wrongParentRedirectUrl = this.dataset.tagRedirect;
        if (wrongParentRedirectUrl) {
          window.location.replace(wrongParentRedirectUrl);
          return;
        }

        const nativeTagUrl = this.nativeTagUrlFor(window.location.href);
        if (nativeTagUrl) {
          window.location.replace(nativeTagUrl);
          return;
        }

        this.onPillClick = this.onPillClick.bind(this);
        this.onPaginationClick = this.onPaginationClick.bind(this);
        this.onPopState = this.onPopState.bind(this);

        this.pills = this.querySelectorAll('a.blog-filtering__pill');
        this.pills.forEach((pill) => {
          pill.addEventListener('click', this.onPillClick);
        });

        document.addEventListener('click', this.onPaginationClick);
        window.addEventListener('popstate', this.onPopState);

        this.syncActiveStates();
      }

      disconnectedCallback() {
        this.pills.forEach((pill) => {
          pill.removeEventListener('click', this.onPillClick);
        });
        document.removeEventListener('click', this.onPaginationClick);
        window.removeEventListener('popstate', this.onPopState);
      }

      onPillClick(event) {
        const pill = event.currentTarget;
        if (this.shouldLetBrowserHandle(event, pill)) return;
        const targetUrl = this.localUrlFrom(pill.getAttribute('href'));
        if (!targetUrl) return;
        event.preventDefault();
        this.navigateViaSRA(targetUrl.pathname + targetUrl.search + targetUrl.hash);
      }

      onPaginationClick(event) {
        if (event.defaultPrevented) return;
        const paginationLink = event.target.closest('a.blog-posts__page-number, a.blog-posts__page-arrow');
        if (!paginationLink) return;
        if (paginationLink.classList.contains('blog-posts__page-arrow--disabled')) return;
        if (this.shouldLetBrowserHandle(event, paginationLink)) return;
        const paginationTargetUrl = this.localUrlFrom(paginationLink.getAttribute('href'));
        if (!paginationTargetUrl) return;
        event.preventDefault();
        this.navigateViaSRA(paginationTargetUrl.pathname + paginationTargetUrl.search);
      }

      shouldLetBrowserHandle(event, anchor) {
        return (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          anchor.target === '_blank'
        );
      }

      localUrlFrom(href) {
        if (!href) return null;
        let url;
        try {
          url = new URL(href, window.location.origin);
        } catch (error) {
          return null;
        }
        if (url.origin !== window.location.origin) return null;
        return url;
      }

      async navigateViaSRA(href) {
        const nativeTagUrl = this.nativeTagUrlFor(href);
        if (nativeTagUrl) href = nativeTagUrl;

        const targetUrl = new URL(href, window.location.origin);
        const postsSectionId = this.findPostsSectionId();

        if (!postsSectionId) {
          window.location.assign(href);
          return;
        }

        try {
          this.setLoading(true);

          const fetchParams = new URLSearchParams(targetUrl.search);
          fetchParams.set('sections', postsSectionId);
          const fetchUrl = `${targetUrl.pathname}?${fetchParams.toString()}`;
          const response = await fetch(fetchUrl);
          if (!response.ok) throw new Error(`SRA fetch failed: ${response.status}`);

          const sectionData = await response.json();
          const postsSectionHtml = sectionData[postsSectionId];
          if (!postsSectionHtml) throw new Error(`Section ${postsSectionId} missing from SRA response`);

          this.swapPostsSection(postsSectionId, postsSectionHtml);

          window.history.pushState({}, '', `${targetUrl.pathname}${targetUrl.search}`);

          this.syncActiveStates();

          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
          console.warn('[blog-filter] SRA swap failed, falling back to navigation', error);
          window.location.assign(href);
        } finally {
          this.setLoading(false);
        }
      }

      onPopState() {
        const postsSectionId = this.findPostsSectionId();
        if (!postsSectionId) return;

        const popParams = new URLSearchParams(window.location.search);
        popParams.set('sections', postsSectionId);
        fetch(`${window.location.pathname}?${popParams.toString()}`)
          .then((response) => response.json())
          .then((sectionData) => {
            const postsSectionHtml = sectionData[postsSectionId];
            if (postsSectionHtml) this.swapPostsSection(postsSectionId, postsSectionHtml);
            this.syncActiveStates();
          })
          .catch(() => {
            window.location.reload();
          });
      }

      findPostsSectionId() {
        const postsRoot = document.querySelector('.blog-posts');
        if (!postsRoot) return null;
        const sectionWrapper = postsRoot.closest('[id^="shopify-section-"]');
        if (!sectionWrapper) return null;
        return sectionWrapper.id.replace(/^shopify-section-/, '');
      }

      swapPostsSection(postsSectionId, postsSectionHtml) {
        const currentWrapper = document.getElementById(`shopify-section-${postsSectionId}`);
        if (!currentWrapper) return;

        const parsedContainer = document.createElement('div');
        parsedContainer.innerHTML = postsSectionHtml.trim();
        const newWrapper = parsedContainer.firstElementChild;
        if (newWrapper && newWrapper.id === currentWrapper.id) {
          currentWrapper.replaceWith(newWrapper);
        } else {
          currentWrapper.innerHTML = postsSectionHtml;
        }

        const reloadedWrapper = document.getElementById(`shopify-section-${postsSectionId}`);
        if (reloadedWrapper) {
          reloadedWrapper.dispatchEvent(
            new CustomEvent('shopify:section:load', {
              bubbles: true,
              detail: { sectionId: postsSectionId }
            })
          );
        }
      }

      setLoading(isLoading) {
        this.classList.toggle('is-loading', isLoading);
      }

      nativeTagUrlFor(href) {
        const candidateUrl = new URL(href, window.location.origin);
        const tagFragment = new URLSearchParams(candidateUrl.hash.replace(/^#/, '')).get('tag');
        if (!tagFragment) return null;
        const blogPathMatch = candidateUrl.pathname.match(/^\/blogs\/([^/]+)\/?$/);
        if (!blogPathMatch) return null;
        return `/blogs/${blogPathMatch[1]}/tagged/${encodeURIComponent(tagFragment)}${candidateUrl.search}`;
      }

      syncActiveStates() {
        const currentPath = window.location.pathname;

        this.querySelectorAll('a.blog-filtering__pill').forEach((pill) => {
          const pillUrl = this.localUrlFrom(pill.getAttribute('href'));
          const isActive = pillUrl ? currentPath === pillUrl.pathname : false;

          pill.classList.toggle('blog-filtering__pill--active', isActive);
          if (isActive) {
            pill.setAttribute('aria-current', 'page');
          } else {
            pill.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}
